import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph, {
  MATURA_FUNCTION_GRAPH_RANGE,
} from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import FractionInput from "../../../../components/inputs/FractionInput";

const EPS = 1e-9;

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function parseNumberLike(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (!raw) return null;

  const frac = raw.match(/^([+-]?\d+)\s*\/\s*([+-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!q) return null;
    return p / q;
  }

  if (/^[+-]?\d*\.?\d+(?:e[+-]?\d+)?$/i.test(raw)) {
    const v = Number(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function graphEquation(a, p, q) {
  return `y=${a}*(x-(${p}))^2+(${q})`;
}

function yValue(a, p, q, x) {
  return a * (x - p) * (x - p) + q;
}

function generateParams() {
  // Wzorujemy parametry na zadaniu otwartym nr 6 (wierzchołek + punkt Oy),
  // ale dodatkowo pilnujemy, by po przesunięciu zawsze było miejsce zerowe x=0.
  const pValues = [-3, -2, -1, 1, 2, 3];
  const qValues = [-4, -3, -2, -1, 1, 2, 3, 4];
  const aValues = [-3, -2, -1, 1, 2, 3];
  const cValues = [-2, -1, 1, 2];
  const xMin = -5;
  const xMax = 5;
  const yMin = -5;
  const yMax = 5;

  const candidates = [];

  for (const p of pValues) {
    for (const q of qValues) {
      for (const c of cValues) {
        const numerator = c - q;
        const denom = p * p;
        if (numerator % denom !== 0) continue;
        const a = numerator / denom;
        if (!aValues.includes(a)) continue;

        const xs = Array.from({ length: xMax - xMin + 1 }, (_, idx) => xMin + idx);
        const ys = xs.map((x) => yValue(a, p, q, x));
        const inRangeCount = ys.filter((y) => y >= yMin && y <= yMax).length;
        if (inRangeCount < 5) continue;

        candidates.push({
          a,
          p,
          q,
          c,
          shift: c,
          sum: 2 * p,
        });
      }
    }
  }

  if (candidates.length > 0) return candidates[randInt(0, candidates.length - 1)];

  return { a: -1, p: 2, q: 3, c: -1, shift: -1, sum: 4 };
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaQuadraticShiftBy3ZerosSumOpenTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: "12.3",
    },
    data: {
      a: -1 / 3,
      p: 3,
      q: 6,
      c: 3,
      shift: 3,
      sum: 6,
      maxPoints: 1,
      xRange: [...MATURA_FUNCTION_GRAPH_RANGE],
      yRange: [...MATURA_FUNCTION_GRAPH_RANGE],
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return generateMay2025OriginalTask();
  }

  const { a, p, q, c, shift, sum } = generateParams();

  return {
    type: "MaturaQuadraticShiftBy3ZerosSumOpenTask",
    data: {
      a,
      p,
      q,
      c,
      shift,
      sum,
      maxPoints: 1,
      xRange: [...MATURA_FUNCTION_GRAPH_RANGE],
      yRange: [...MATURA_FUNCTION_GRAPH_RANGE],
    },
  };
}

export function renderTask({ data }) {
  const { a, p, q, c, shift, xRange, yRange } = data;
  const shiftExpr =
    shift >= 0 ? `g(x)=f(x)-${shift}` : `g(x)=f(x)+${Math.abs(shift)}`;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - otwarte (0-1 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math={"(x,y)"} />{" "}
        przedstawiono fragment paraboli, która jest wykresem funkcji kwadratowej{" "}
        <InlineMath math={"f"} /> (zobacz rysunek).
      </p>

      <p>
        Wierzchołek tej paraboli ma współrzędne{" "}
        <InlineMath math={`(p,q)=(${p},\\;${q})`} />.
        Ta parabola przecina oś <InlineMath math={"Oy"} /> w punkcie{" "}
        <InlineMath math={`(0,\\;${c})`} />.
      </p>

      <MaturaFunctionGraph
        equations={[graphEquation(a, p, q)]}
        xRange={xRange}
        yRange={yRange}
      />

      <p>
        Funkcja <InlineMath math={"g"} /> jest określona dla każdej liczby
        rzeczywistej <InlineMath math={"x"} /> wzorem{" "}
        <InlineMath math={shiftExpr} />.
      </p>

      <p>
        Liczby <InlineMath math={"x_1"} /> oraz <InlineMath math={"x_2"} /> są
        różnymi miejscami zerowymi funkcji <InlineMath math={"g"} />.
      </p>

      <p>
        Oblicz sumę <InlineMath math={"x_1+x_2"} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 6, maxWidth: 360 }}>
      <div className="max-w-[240px]">
        <FractionInput
          value={userAnswer.sum}
          onChange={(val) => onChange({ ...(userAnswer || {}), sum: val })}
          disabled={disabled}
          label="x_1+x_2"
          placeholder="np. 6"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const expected = data.sum;
  const userSum = parseNumberLike(userAnswer.sum);

  const correct = userSum != null && Math.abs(userSum - expected) < EPS;

  return {
    isCorrect: correct,
    score: correct ? 1 : 0,
    maxScore: data.maxPoints ?? 1,
    parts: { sum: correct },
  };
}

function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw", "value", "text", "expr", "equation", "latex"]) {
      if (typeof val[k] === "string" && val[k].trim() !== "") return val[k];
    }
  }
  return "";
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const raw = getRaw(userAnswer.sum);
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`x_1+x_2=${raw}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x_1+x_2=${data.sum}`} />;
}

export function renderExplanation({ data }) {
  const { p, c, shift, sum } = data;
  const shiftExpr =
    shift >= 0 ? `g(x)=f(x)-${shift}` : `g(x)=f(x)+${Math.abs(shift)}`;
  const eqFx = `f(x)=${shift}`;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Z warunku <InlineMath math={shiftExpr} />{" "}
        miejsca zerowe funkcji <InlineMath math={"g"} /> spełniają równanie{" "}
        <InlineMath math={eqFx} />.
      </p>

      <p>
        Z wykresu widać, że dla <InlineMath math={"x=0"} /> mamy{" "}
        <InlineMath math={`f(0)=${c}`} />, więc{" "}
        <InlineMath math={"x_1=0"} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Oś symetrii paraboli ma równanie{" "}
        <InlineMath math={`x=${p}`} />, więc argumenty dające tę samą wartość
        funkcji są symetryczne względem tej osi.
      </p>

      <BlockMath math="\frac{x_1+x_2}{2}=p" />
      <BlockMath math={`x_2=2p-x_1=2\\cdot${p < 0 ? `(${p})` : p}-0=${sum}`} />

      <p>
        Zatem:
      </p>
      <BlockMath math={`x_1+x_2=0${sum < 0 ? "-" : "+"}${Math.abs(sum)}=${sum}`} />
    </div>
  );
}
